import { type ReactNode, type ComponentPropsWithoutRef } from 'react';

import { Link } from '@tanstack/react-router';

import { Button as ShadcnButton } from '../shadcn/Button';
import type { TButtonVariant } from '../shadcn/Button';
import { TSizes, TColors } from '../../types';

interface IButtonBaseProps {
  variant?: TButtonVariant;
  size?: TSizes;
  color?: TColors;
  iconLeft?: ReactNode;
  iconRight?: ReactNode;
  children?: ReactNode;
  className?: string;
  isDividerVisible?: boolean;
}

type TButtonAsButton = IButtonBaseProps &
  Omit<ComponentPropsWithoutRef<'button'>, keyof IButtonBaseProps> & {
    as?: 'button';
    to?: never;
    href?: never;
  };

type TButtonAsLink = IButtonBaseProps &
  Omit<ComponentPropsWithoutRef<typeof Link>, keyof IButtonBaseProps> & {
    as: 'link';
    to: string;
    href?: never;
  };

type TButtonAsAnchor = IButtonBaseProps &
  Omit<ComponentPropsWithoutRef<'a'>, keyof IButtonBaseProps> & {
    as: 'a';
    href: string;
    to?: never;
  };

type TButtonProps = TButtonAsButton | TButtonAsLink | TButtonAsAnchor;

export function Button(props: TButtonProps) {
  const {
    as,
    children,
    className,
    color = 'primary',
    iconLeft,
    iconRight,
    isDividerVisible = true,
    size = 'm',
    variant = 'filled',
    ...restProps
  } = props;

  const hasText = Boolean(children);
  const showDivider = isDividerVisible && hasText && (iconLeft || iconRight);

  const content = (
    <>
      {iconLeft && (
        <>
          {iconLeft}
          {showDivider && <div className="w-px h-6 bg-current opacity-30" />}
        </>
      )}
      {hasText && <span className="w-full text-center">{children}</span>}
      {iconRight && (
        <>
          {showDivider && <div className="w-px h-6 bg-current opacity-30" />}
          {iconRight}
        </>
      )}
    </>
  );

  if (as === 'link') {
    const { to, ...linkProps } = restProps as TButtonAsLink;
    return (
      <ShadcnButton
        color={color}
        size={size}
        variant={variant}
        className={className}
        asChild
      >
        <Link to={to} {...linkProps}>
          {content}
        </Link>
      </ShadcnButton>
    );
  }

  if (as === 'a') {
    const { href, ...anchorProps } = restProps as TButtonAsAnchor;
    return (
      <ShadcnButton
        color={color}
        size={size}
        variant={variant}
        className={className}
        asChild
      >
        <a href={href} {...anchorProps}>
          {content}
        </a>
      </ShadcnButton>
    );
  }

  const { ...buttonProps } = restProps as TButtonAsButton;

  return (
    <ShadcnButton
      color={color}
      size={size}
      variant={variant}
      className={className}
      {...buttonProps}
    >
      {content}
    </ShadcnButton>
  );
}
